import React, { FormEvent, useState } from 'react'
import { useForm, Link, router } from '@inertiajs/react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Collapse from '@mui/material/Collapse'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'
import { ColorPicker } from '@/components/shared/ColorPicker'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { Markdown } from '@/components/Markdown'

interface BoardAgent {
  id: number
  role: string
  agent: {
    id: number
    username: string
    agent_type: string
  }
  created_at: string
}

interface BoardAvailableAgent {
  id: number
  username: string
  agent_type: string
}

interface BoardData {
  id: number
  name: string
  slug: string
  color: string | null
  task_count: number
  member_count: number
  created_at: string
  board_agents: BoardAgent[]
  available_agents: BoardAvailableAgent[]
}

interface ProjectAgentData {
  id: number
  role: 'admin' | 'member'
  agent: {
    id: number
    username: string
    slug: string
    agent_type: 'human' | 'machine'
  }
  created_at: string
}

interface AvailableAgent {
  id: number
  username: string
  slug: string
}

interface Props {
  project: { id: number; name: string; slug: string; color: string | null; description: string | null; archived: boolean; archived_at: string | null }
  project_agents: ProjectAgentData[]
  available_agents: AvailableAgent[]
  boards: BoardData[]
}

function BoardForm({ projectId }: { projectId: number }) {
  const { data, setData, post, processing, errors, clearErrors, reset } = useForm({
    board: { name: '' },
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    clearErrors()
    post(`/manage/projects/${projectId}/boards`, {
      onSuccess: () => reset(),
    })
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mt: 2, flexWrap: 'wrap' }}>
      <TextField
        size="small"
        label="Board name"
        value={data.board.name}
        onChange={e => setData('board', { ...data.board, name: e.target.value })}
        error={'board.name' in errors}
        helperText={(errors as Record<string, string>)['board.name']}
        sx={{ flex: 1, minWidth: 200 }}
      />
      <Button type="submit" variant="contained" size="medium" disabled={processing}>
        Create board
      </Button>
    </Box>
  )
}

function BoardEditRow({ board, projectId, onCancel }: { board: BoardData; projectId: number; onCancel: () => void }) {
  const [name, setName] = useState(board.name)
  const [color, setColor] = useState(board.color || '#6B7280')
  const [saving, setSaving] = useState(false)

  function handleSave() {
    setSaving(true)
    router.put(`/manage/projects/${projectId}/boards/${board.id}`, {
      board: { name, color },
    }, {
      onFinish: () => setSaving(false),
      onSuccess: () => onCancel(),
    })
  }

  return (
    <TableRow>
      <TableCell colSpan={3}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField
            size="small"
            label="Name"
            value={name}
            onChange={e => setName(e.target.value)}
            sx={{ maxWidth: 300 }}
          />
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Color</Typography>
            <ColorPicker value={color} onChange={setColor} />
          </Box>
        </Box>
      </TableCell>
      <TableCell />
      <TableCell align="right">
        <Button size="small" onClick={handleSave} disabled={saving} variant="contained" sx={{ mr: 1 }}>
          Save
        </Button>
        <Button size="small" onClick={onCancel}>
          Cancel
        </Button>
      </TableCell>
    </TableRow>
  )
}

function BoardMembersSection({ board, projectId }: { board: BoardData; projectId: number }) {
  const [selectedAgentId, setSelectedAgentId] = useState<number | ''>('')
  const [selectedRole, setSelectedRole] = useState('member')
  const [removeMemberTarget, setRemoveMemberTarget] = useState<BoardAgent | null>(null)

  function handleAddMember() {
    if (selectedAgentId) {
      router.post(`/manage/projects/${projectId}/boards/${board.id}/add_member`, {
        agent_id: selectedAgentId,
        role: selectedRole,
      })
      setSelectedAgentId('')
      setSelectedRole('member')
    }
  }

  function handleRoleChange(member: BoardAgent, newRole: string) {
    router.put(`/manage/projects/${projectId}/boards/${board.id}/update_member/${member.id}`, {
      role: newRole,
    })
  }

  function handleRemoveMember() {
    if (removeMemberTarget) {
      router.delete(`/manage/projects/${projectId}/boards/${board.id}/remove_member/${removeMemberTarget.id}`)
      setRemoveMemberTarget(null)
    }
  }

  return (
    <Box sx={{ pl: 4, pr: 2, pb: 2 }}>
      {board.available_agents.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 180, flexGrow: 1 }}>
            <InputLabel>Add agent</InputLabel>
            <Select
              value={selectedAgentId}
              label="Add agent"
              onChange={e => setSelectedAgentId(e.target.value as number)}
            >
              {board.available_agents.map(a => (
                <MenuItem key={a.id} value={a.id}>{a.username} ({a.agent_type})</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ width: 130 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={selectedRole}
              label="Role"
              onChange={e => setSelectedRole(e.target.value)}
            >
              <MenuItem value="member">Member</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" size="small" onClick={handleAddMember} disabled={!selectedAgentId}>
            Add
          </Button>
        </Box>
      )}

      {board.board_agents.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No members in this board.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Agent</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Role</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {board.board_agents.map((member) => (
              <TableRow key={member.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{member.agent.username}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={member.agent.agent_type === 'human' ? 'Human' : 'Machine'}
                    size="small"
                    variant="outlined"
                    color={member.agent.agent_type === 'machine' ? 'secondary' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={member.role}
                      onChange={e => handleRoleChange(member, e.target.value)}
                      size="small"
                    >
                      <MenuItem value="member">Member</MenuItem>
                      <MenuItem value="admin">Admin</MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell align="right">
                  <Button size="small" color="error" onClick={() => setRemoveMemberTarget(member)}>
                    Remove
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <ConfirmDialog
        open={!!removeMemberTarget}
        title="Remove board member"
        message={`Remove ${removeMemberTarget?.agent.username} from this board?`}
        confirmLabel="Remove"
        onConfirm={handleRemoveMember}
        onCancel={() => setRemoveMemberTarget(null)}
      />
    </Box>
  )
}

function MemberForm({ projectId, availableAgents }: { projectId: number; availableAgents: AvailableAgent[] }) {
  const { data, setData, post, processing, errors, clearErrors, reset } = useForm({
    project_agent: {
      agent_id: '' as number | '',
      role: 'member',
    },
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    clearErrors()
    post(`/manage/projects/${projectId}/members`, {
      onSuccess: () => reset(),
    })
  }

  if (availableAgents.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        All agents are already members of this project.
      </Typography>
    )
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mt: 2, flexWrap: 'wrap' }}>
      <FormControl size="small" sx={{ flex: 1 }} error={'project_agent.agent_id' in errors}>
        <InputLabel>Agent</InputLabel>
        <Select
          value={data.project_agent.agent_id}
          label="Agent"
          onChange={e => setData('project_agent', { ...data.project_agent, agent_id: e.target.value as number })}
        >
          {availableAgents.map((a) => (
            <MenuItem key={a.id} value={a.id}>{a.username}</MenuItem>
          ))}
        </Select>
        {'project_agent.agent_id' in errors && (
          <FormHelperText>{(errors as Record<string, string>)['project_agent.agent_id']}</FormHelperText>
        )}
      </FormControl>
      <FormControl size="small" sx={{ width: 150 }}>
        <InputLabel>Role</InputLabel>
        <Select
          value={data.project_agent.role}
          label="Role"
          onChange={e => setData('project_agent', { ...data.project_agent, role: e.target.value })}
        >
          <MenuItem value="member">Member</MenuItem>
          <MenuItem value="admin">Admin</MenuItem>
        </Select>
      </FormControl>
      <Button type="submit" variant="contained" size="medium" disabled={processing}>
        Add
      </Button>
    </Box>
  )
}

export default function Show({ project, project_agents, available_agents, boards }: Props) {
  const [editingBoardId, setEditingBoardId] = useState<number | null>(null)
  const [expandedBoardId, setExpandedBoardId] = useState<number | null>(null)
  const [deleteBoardTarget, setDeleteBoardTarget] = useState<BoardData | null>(null)
  const [removeMemberTarget, setRemoveMemberTarget] = useState<ProjectAgentData | null>(null)
  const [deleteProjectConfirm, setDeleteProjectConfirm] = useState(false)

  function handleRoleChange(pa: ProjectAgentData, newRole: string) {
    router.put(`/manage/projects/${project.id}/members/${pa.id}`, {
      project_agent: { role: newRole },
    })
  }

  function handleRemoveMember() {
    if (removeMemberTarget) {
      router.delete(`/manage/projects/${project.id}/members/${removeMemberTarget.id}`)
      setRemoveMemberTarget(null)
    }
  }

  function handleDeleteBoard() {
    if (deleteBoardTarget) {
      router.delete(`/manage/projects/${project.id}/boards/${deleteBoardTarget.id}`)
      setDeleteBoardTarget(null)
    }
  }

  function handleDeleteProject() {
    router.delete(`/manage/projects/${project.id}`)
    setDeleteProjectConfirm(false)
  }

  function handleArchive() {
    router.patch(`/manage/projects/${project.id}/archive`)
  }

  function handleRestore() {
    router.patch(`/manage/projects/${project.id}/unarchive`)
  }

  function toggleBoardExpand(boardId: number) {
    setExpandedBoardId(expandedBoardId === boardId ? null : boardId)
  }

  return (
    <SidebarLayout section="manage" title={project.name}>
      <Box sx={{ maxWidth: 900 }}>
        {project.archived && (
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'warning.light', backgroundColor: 'warning.lighter', borderRadius: 2, p: 2, mb: 2 }}>
            <Typography variant="body2">
              This project is archived{project.archived_at ? ` (since ${new Date(project.archived_at).toLocaleDateString()})` : ''}. Its boards, notebooks, tasks, channels, and session logs are hidden from default views. Restore the project to make them visible again.
            </Typography>
          </Paper>
        )}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button component={Link} href="/manage/projects" size="small">
            Back to projects
          </Button>
          <Button component={Link} href={`/manage/projects/${project.id}/edit`} size="small">
            Edit project
          </Button>
          {project.archived ? (
            <Button size="small" onClick={handleRestore}>
              Restore project
            </Button>
          ) : (
            <Button size="small" onClick={handleArchive}>
              Archive project
            </Button>
          )}
          <Button size="small" color="error" onClick={() => setDeleteProjectConfirm(true)}>
            Delete project
          </Button>
        </Box>

        {project.description && (
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mb: 3 }}>
            <Markdown variant="body2">{project.description}</Markdown>
          </Paper>
        )}

        {/* Boards Section */}
        <Typography variant="h2" sx={{ mb: 2 }}>Boards</Typography>
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', overflowX: 'auto', mb: 1 }}>
          <Table size="small" sx={{ minWidth: 500 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell>Board</TableCell>
                <TableCell align="center">Tasks</TableCell>
                <TableCell align="center">Members</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {boards.map((board) => (
                editingBoardId === board.id ? (
                  <BoardEditRow
                    key={board.id}
                    board={board}
                    projectId={project.id}
                    onCancel={() => setEditingBoardId(null)}
                  />
                ) : (
                  <React.Fragment key={board.id}>
                    <TableRow>
                      <TableCell sx={{ width: 40, pr: 0 }}>
                        <IconButton size="small" onClick={() => toggleBoardExpand(board.id)}>
                          {expandedBoardId === board.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: board.color || '#6B7280',
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant="body2" fontWeight={500}>
                            {board.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color="text.secondary">{board.task_count}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color="text.secondary">{board.member_count}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(board.created_at).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" sx={{ mr: 1 }} onClick={() => setEditingBoardId(board.id)}>
                          Edit
                        </Button>
                        <Button size="small" color="error" onClick={() => setDeleteBoardTarget(board)}>
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 0, borderBottom: expandedBoardId === board.id ? undefined : 'none' }}>
                        <Collapse in={expandedBoardId === board.id} timeout="auto" unmountOnExit>
                          <BoardMembersSection board={board} projectId={project.id} />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                )
              ))}
              {boards.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No boards in this project.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <BoardForm projectId={project.id} />

        {/* Members Section */}
        <Typography variant="h2" sx={{ mb: 2, mt: 4 }}>Members</Typography>
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 500 }}>
            <TableHead>
              <TableRow>
                <TableCell>Agent</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Role</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {project_agents.map((pa) => (
                <TableRow key={pa.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {pa.agent.username}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={pa.agent.agent_type === 'human' ? 'Human' : 'Machine'}
                      size="small"
                      variant="outlined"
                      color={pa.agent.agent_type === 'machine' ? 'secondary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={pa.role}
                        onChange={e => handleRoleChange(pa, e.target.value)}
                        size="small"
                      >
                        <MenuItem value="member">Member</MenuItem>
                        <MenuItem value="admin">Admin</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" color="error" onClick={() => setRemoveMemberTarget(pa)}>
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {project_agents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No members.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <MemberForm projectId={project.id} availableAgents={available_agents} />
      </Box>

      <ConfirmDialog
        open={!!deleteBoardTarget}
        title="Delete board"
        message={`Are you sure you want to delete board '${deleteBoardTarget?.name}'? All tasks in this board will also be deleted.`}
        onConfirm={handleDeleteBoard}
        onCancel={() => setDeleteBoardTarget(null)}
      />

      <ConfirmDialog
        open={!!removeMemberTarget}
        title="Remove member"
        message={`Remove ${removeMemberTarget?.agent.username} from this project? They will lose access to all project resources.`}
        confirmLabel="Remove"
        onConfirm={handleRemoveMember}
        onCancel={() => setRemoveMemberTarget(null)}
      />

      <ConfirmDialog
        open={deleteProjectConfirm}
        title="Delete project"
        message={`Are you sure you want to delete project '${project.name}'? This will also delete all associated boards, notebooks, and webhooks.`}
        onConfirm={handleDeleteProject}
        onCancel={() => setDeleteProjectConfirm(false)}
      />
    </SidebarLayout>
  )
}
